import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "cmdk";
import { Building2, Cpu, Search, User as UserIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

import { cn } from "@/lib/utils";
import { useUsers } from "@/hooks/useUsers";
import { useDevices } from "@/hooks/useDevices";
import { useOrganisations } from "@/hooks/useOrganisations";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

type SearchTarget =
  | { type: "user"; id: number }
  | { type: "device"; id: number }
  | { type: "organisation"; id: number };

interface SidebarSearchProps {
  isRail: boolean;
  /** Called after a result is selected (e.g. to close the mobile drawer). */
  onNavigate?: () => void;
  /** Called when search is triggered from the collapsed rail (no room to type there). */
  onExpand?: () => void;
}

export function SidebarSearch({
  isRail,
  onNavigate,
  onExpand,
}: SidebarSearchProps) {
  const { t } = useTranslation("layout");
  const navigate = useNavigate();
  const { data: users } = useUsers();
  const { data: devices } = useDevices();
  const { data: organisations } = useOrganisations();

  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const wasRail = useRef(isRail);

  // When search is opened from the collapsed rail, the sidebar expands first
  // (there's no room to type in rail mode) — focus the input once it appears.
  useEffect(() => {
    if (wasRail.current && !isRail) {
      const raf = requestAnimationFrame(() => inputRef.current?.focus());
      wasRail.current = isRail;
      return () => cancelAnimationFrame(raf);
    }
    wasRail.current = isRail;
  }, [isRail]);

  useEffect(() => {
    function handlePointerDown(event: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, []);

  function handleSelect(target: SearchTarget) {
    navigate(`/${target.type}/${target.id}`);
    setQuery("");
    setOpen(false);
    onNavigate?.();
  }

  function handleKeyDown(event: React.KeyboardEvent) {
    if (event.key === "Escape") {
      setOpen(false);
      inputRef.current?.blur();
    }
  }

  if (isRail) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={() => onExpand?.()}
            className="flex w-full items-center justify-center rounded-lg px-2 py-2 text-slate-600 transition-colors hover:bg-slate-200 hover:text-slate-900 dark:text-[#8ba1b7] dark:hover:bg-white/5 dark:hover:text-white"
            aria-label={t("sidebarSearch.search")}
          >
            <Search className="size-5" />
          </button>
        </TooltipTrigger>
        <TooltipContent side="right">{t("sidebarSearch.search")}</TooltipContent>
      </Tooltip>
    );
  }

  return (
    <div ref={containerRef} className="relative">
      <Command
        shouldFilter
        onKeyDown={handleKeyDown}
        className="flex w-full flex-col"
      >
        <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 dark:border-white/10 dark:bg-white/5">
          <Search className="size-4 shrink-0 text-slate-400 dark:text-[#8ba1b7]" />
          <CommandInput
            ref={inputRef}
            value={query}
            onValueChange={setQuery}
            onFocus={() => setOpen(true)}
            placeholder={t("sidebarSearch.placeholder")}
            className="w-full min-w-0 bg-transparent text-sm text-slate-700 outline-none placeholder:text-slate-400 dark:text-[#8ba1b7] dark:placeholder:text-[#8ba1b7]/70"
          />
        </div>

        <CommandList
          className={cn(
            "absolute inset-x-0 top-full z-50 mt-1 max-h-72 overflow-y-auto rounded-lg border border-slate-200 bg-white p-1 shadow-lg dark:border-white/10 dark:bg-[#20222a]",
            open ? "block" : "hidden",
          )}
        >
          <CommandEmpty className="px-3 py-6 text-center text-sm text-slate-500 dark:text-[#8ba1b7]">
            {t("sidebarSearch.noResultsFound")}
          </CommandEmpty>

          <CommandGroup
            heading={t("sidebarSearch.users")}
            className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-slate-400 dark:[&_[cmdk-group-heading]]:text-[#8ba1b7]"
          >
            {users?.map((user) => (
              <CommandItem
                key={`user-${user.id}`}
                value={user.email}
                onSelect={() => handleSelect({ type: "user", id: user.id })}
                className="flex cursor-default select-none items-center gap-2 rounded-md px-2 py-1.5 text-sm text-slate-700 outline-none aria-selected:bg-slate-100 aria-selected:text-slate-900 dark:text-[#c3d1de] dark:aria-selected:bg-white/10 dark:aria-selected:text-white"
              >
                <UserIcon className="size-4 shrink-0 text-slate-400 dark:text-[#8ba1b7]" />
                <span className="truncate">{user.email}</span>
              </CommandItem>
            ))}
          </CommandGroup>

          <CommandGroup
            heading={t("sidebarSearch.devices")}
            className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-slate-400 dark:[&_[cmdk-group-heading]]:text-[#8ba1b7]"
          >
            {devices?.map((device) => (
              <CommandItem
                key={`device-${device.id}`}
                value={`${device.name} ${device.dev_eui}`}
                onSelect={() =>
                  handleSelect({ type: "device", id: device.id })
                }
                className="flex cursor-default select-none items-center gap-2 rounded-md px-2 py-1.5 text-sm text-slate-700 outline-none aria-selected:bg-slate-100 aria-selected:text-slate-900 dark:text-[#c3d1de] dark:aria-selected:bg-white/10 dark:aria-selected:text-white"
              >
                <Cpu className="size-4 shrink-0 text-slate-400 dark:text-[#8ba1b7]" />
                <span className="truncate">
                  {device.name} ({device.dev_eui})
                </span>
              </CommandItem>
            ))}
          </CommandGroup>

          <CommandGroup
            heading={t("sidebarSearch.organisations")}
            className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-slate-400 dark:[&_[cmdk-group-heading]]:text-[#8ba1b7]"
          >
            {organisations?.map((org) => (
              <CommandItem
                key={`organisation-${org.id}`}
                value={org.name}
                onSelect={() =>
                  handleSelect({ type: "organisation", id: org.id })
                }
                className="flex cursor-default select-none items-center gap-2 rounded-md px-2 py-1.5 text-sm text-slate-700 outline-none aria-selected:bg-slate-100 aria-selected:text-slate-900 dark:text-[#c3d1de] dark:aria-selected:bg-white/10 dark:aria-selected:text-white"
              >
                <Building2 className="size-4 shrink-0 text-slate-400 dark:text-[#8ba1b7]" />
                <span className="truncate">{org.name}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        </CommandList>
      </Command>
    </div>
  );
}
